import { MenuItem } from "@chakra-ui/react";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { VThreeDotMenu } from "components/Menu/ThreeDotMenu";
import { PropsWithChildren, useCallback } from "react";
import CaminoSkip from "components/Icon/CaminoSkip";
import MenuItemIcon from "components/Icon/MenuItemIcon";
import { Task, TaskId } from "features/Resources/Task/models";
import { useTaskDetailQuery } from "features/Resources/Task/hooks";
import { Link as RouterLink } from "react-router";
import CaminoAnnounce from "components/Icon/CaminoAnnounce";
import { useForceSendByPrefixId, useSkipByPrefixId } from "features/Resources/Shared/hooks";
import { taskStartNowArgs } from "features/Resources/Shared/helpers/confirmationArgs";
import { MdOutlineStart } from "react-icons/md";
import { taskPathById } from "helpers/url";
import CaminoEye from "components/Icon/CaminoEye";
import { FiCheckCircle, FiRotateCcw } from "react-icons/fi";
import Template from "components/Icon/Iconify/Template";
import { taskTemplatePathById } from "helpers/url/pages/v2/actionPaths";
import useCaminoStore from "stores/caminoStore";
import { NO_ACTION_STATUSES } from "../../constants";
import { useMarkCompleteTask } from "../../hooks";
import { TaskActionMenuButton } from "../Action/Button";

type ThreeDotOption = "view" | "start_now" | "remind" | "mark_complete" | "view_template" | "skip" | "remove" | "task_action";

export default function TaskThreeDotOptionMenu({ task, exclude = [], onOpenRemind }: { task: Task | undefined, exclude?: Array<ThreeDotOption>, onOpenRemind?: () => void }) {
  const currentUser = useCaminoStore((state) => state.currentUser);
  const { mutate: startNow } = useForceSendByPrefixId(task?.prefixId);
  const { mutate: handleSkip } = useSkipByPrefixId(task?.prefixId);
  const markComplete = useMarkCompleteTask(task);

  const handleRemind = useCallback(() => {
    onOpenRemind?.();
  }, [onOpenRemind]);

  const isCurrentUserAssignee = task?.assigneeId === currentUser?.id;

  if (!task) return null;

  return (
    <VThreeDotMenu size="xs" inPortal>
      {!exclude.includes("view") && (
        <MenuItem as={RouterLink} to={taskPathById(task.id)} icon={<MenuItemIcon icon={CaminoEye} />}>View</MenuItem>
      )}
      {!exclude.includes("start_now") && task.status === "upcoming" && (
        <ButtonWithConfirm
          {...taskStartNowArgs()}
          intent="confirmation"
          handleConfirm={() => startNow()}
        >
          <MenuItem icon={<MenuItemIcon icon={MdOutlineStart} />}>Start Now</MenuItem>
        </ButtonWithConfirm>
      )}
      {!exclude.includes("remind") && !NO_ACTION_STATUSES.includes(task.status) && !isCurrentUserAssignee && (
        <MenuItem icon={<MenuItemIcon icon={CaminoAnnounce} />} onClick={handleRemind}>
          Remind
        </MenuItem>
      )}
      {!exclude.includes("mark_complete") && task.status !== "skipped" && (
        <MenuItem icon={<MenuItemIcon icon={task?.status === "completed" ? FiRotateCcw : FiCheckCircle} />} onClick={() => markComplete()}>
          {task?.status === "completed" ? "Re-Open" : "Mark Complete"}
        </MenuItem>
      )}
      {!exclude.includes("skip") && task.status !== "completed" && (
        <MenuItem icon={<MenuItemIcon icon={CaminoSkip} />} onClick={() => handleSkip()}>
          {task.status === "skipped" ? "Unskip" : "Skip"}
        </MenuItem>
      )}
      {!exclude.includes("task_action") && task.status !== "completed" && task.taskAction && (
        <TaskActionMenuButton taskId={task.id} taskAction={task.taskAction} />
      )}
      {!exclude.includes("view_template") && (
        <MenuItem as={RouterLink} to={taskTemplatePathById(task.templateId)} icon={<MenuItemIcon icon={Template} />}>View Template</MenuItem>
      )}
    </VThreeDotMenu>
  );
}

interface taskThreeDotOptionMenuProps extends PropsWithChildren {
  id: TaskId;
  removeOnly?: boolean;
}

export function TaskThreeDotOptionMenuById({ id, removeOnly = false }: taskThreeDotOptionMenuProps) {
  const { data: task } = useTaskDetailQuery(id);

  if (!task) {
    return null;
  }

  return <TaskThreeDotOptionMenu task={task} />;
}
